import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import moment from "moment";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ReportHeader from "../components/ReportHeader";
import {
  GET_AAU_ORG_DAILY,
  GET_AAU_RECENT_ERRORS,
  GET_AAU_ROUTE_DAILY,
  GET_AAU_USER_DAILY,
} from "./queries";

const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

const EVENT_TYPES = [
  "page_view",
  "filter_change",
  "export",
  "error_shown",
  "empty_state",
  "search",
] as const;

const EVENT_COLORS: Record<string, string> = {
  page_view: "#2563eb",
  filter_change: "#16a34a",
  export: "#9333ea",
  error_shown: "#dc2626",
  empty_state: "#ca8a04",
  search: "#0891b2",
};

interface RouteDailyRow {
  day: string;
  route_id: string | null;
  route_label: string | null;
  path: string;
  event_type: string;
  event_count: number;
  unique_users: number;
  unique_orgs: number;
}

interface UserDailyRow {
  day: string;
  user_id: string;
  username: string | null;
  event_count: number;
  unique_routes: number;
}

interface OrgDailyRow {
  day: string;
  organization_id: number | null;
  organization_name: string | null;
  event_count: number;
  unique_users: number;
}

interface ErrorRow {
  id: string;
  viewed_at: string;
  route_id: string | null;
  route_label: string | null;
  path: string;
  user_id: string;
  organization_id: number | null;
  error_code: string | null;
  payload: Record<string, unknown> | null;
}

const formatNumber = (n: number) => n.toLocaleString();

const sectionTitle = "text-lg font-semibold mb-3 text-gray-800";
const card = "bg-white rounded-md p-4 mb-4 shadow-sm";
const th = "text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 border-b border-gray-200";
const td = "text-sm text-gray-800 px-3 py-2 border-b border-gray-100";

const client = createApolloClient();

export const AdminAppUsageReport = () => {
  const [days, setDays] = useState(30);

  const startDay = useMemo(
    () => moment().subtract(days - 1, "days").startOf("day").format("YYYY-MM-DD"),
    [days]
  );
  const startAt = useMemo(
    () => moment().subtract(days - 1, "days").startOf("day").toISOString(),
    [days]
  );

  const { data: routeData, loading: routeLoading } = useQuery(
    GET_AAU_ROUTE_DAILY,
    { client, variables: { startDay }, fetchPolicy: "cache-and-network" }
  );
  const { data: userData, loading: userLoading } = useQuery(
    GET_AAU_USER_DAILY,
    { client, variables: { startDay }, fetchPolicy: "cache-and-network" }
  );
  const { data: orgData, loading: orgLoading } = useQuery(
    GET_AAU_ORG_DAILY,
    { client, variables: { startDay }, fetchPolicy: "cache-and-network" }
  );
  const { data: errorData, loading: errorLoading } = useQuery(
    GET_AAU_RECENT_ERRORS,
    {
      client,
      variables: { startAt, limit: 50 },
      fetchPolicy: "cache-and-network",
    }
  );

  const routeRows = (routeData?.v_aau_route_daily ?? []) as RouteDailyRow[];
  const userRows = (userData?.v_aau_user_daily ?? []) as UserDailyRow[];
  const orgRows = (orgData?.v_aau_org_daily ?? []) as OrgDailyRow[];
  const errorRows = (errorData?.admin_analytics_events ?? []) as ErrorRow[];

  const dailyChart = useMemo(() => {
    const byDay = new Map<string, Record<string, number>>();
    for (const row of routeRows) {
      const bucket = byDay.get(row.day) ?? { day: 0 } as unknown as Record<string, number>;
      const current = byDay.get(row.day) ?? ({} as Record<string, number>);
      current[row.event_type] = (current[row.event_type] ?? 0) + row.event_count;
      byDay.set(row.day, current);
    }
    const filled: { day: string; [key: string]: string | number }[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = moment().subtract(i, "days").format("YYYY-MM-DD");
      const counts = byDay.get(d) ?? {};
      filled.push({
        day: moment(d).format("MMM D"),
        ...EVENT_TYPES.reduce(
          (acc, type) => ({ ...acc, [type]: counts[type] ?? 0 }),
          {} as Record<string, number>
        ),
      });
    }
    return filled;
  }, [routeRows, days]);

  const topRoutes = useMemo(() => {
    const byRoute = new Map<
      string,
      {
        route_id: string;
        route_label: string | null;
        path: string;
        counts: Record<string, number>;
        total: number;
      }
    >();
    for (const row of routeRows) {
      if (!row.route_id) continue;
      const key = row.route_id;
      const existing = byRoute.get(key) ?? {
        route_id: row.route_id,
        route_label: row.route_label,
        path: row.path,
        counts: {} as Record<string, number>,
        total: 0,
      };
      existing.counts[row.event_type] =
        (existing.counts[row.event_type] ?? 0) + row.event_count;
      existing.total += row.event_count;
      if (!existing.route_label && row.route_label) {
        existing.route_label = row.route_label;
      }
      byRoute.set(key, existing);
    }
    return Array.from(byRoute.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 25);
  }, [routeRows]);

  const topUsers = useMemo(() => {
    const byUser = new Map<
      string,
      { user_id: string; username: string | null; events: number; routes: number; last_seen: string }
    >();
    for (const row of userRows) {
      const existing = byUser.get(row.user_id) ?? {
        user_id: row.user_id,
        username: row.username,
        events: 0,
        routes: 0,
        last_seen: row.day,
      };
      existing.events += row.event_count;
      existing.routes = Math.max(existing.routes, row.unique_routes);
      if (row.day > existing.last_seen) existing.last_seen = row.day;
      if (!existing.username && row.username) existing.username = row.username;
      byUser.set(row.user_id, existing);
    }
    return Array.from(byUser.values())
      .sort((a, b) => b.events - a.events)
      .slice(0, 25);
  }, [userRows]);

  const topOrgs = useMemo(() => {
    const byOrg = new Map<
      number,
      { organization_id: number; organization_name: string | null; events: number; users: number; last_seen: string }
    >();
    for (const row of orgRows) {
      if (row.organization_id === null) continue;
      const existing = byOrg.get(row.organization_id) ?? {
        organization_id: row.organization_id,
        organization_name: row.organization_name,
        events: 0,
        users: 0,
        last_seen: row.day,
      };
      existing.events += row.event_count;
      existing.users = Math.max(existing.users, row.unique_users);
      if (row.day > existing.last_seen) existing.last_seen = row.day;
      if (!existing.organization_name && row.organization_name) {
        existing.organization_name = row.organization_name;
      }
      byOrg.set(row.organization_id, existing);
    }
    return Array.from(byOrg.values())
      .sort((a, b) => b.events - a.events)
      .slice(0, 25);
  }, [orgRows]);

  const isLoading = routeLoading && userLoading && orgLoading && errorLoading;

  return (
    <div>
      <div className={card}>
        <ReportHeader title="Admin app usage" />
        <div className="flex gap-2 mt-2">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.days}
              onClick={() => setDays(preset.days)}
              className={`px-3 py-1 text-sm rounded-md border ${
                days === preset.days
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className={card}>
        <div className={sectionTitle}>Daily activity</div>
        {isLoading && dailyChart.every((d) =>
          EVENT_TYPES.every((t) => (d as Record<string, number>)[t] === 0)
        ) ? (
          <div className="text-sm text-gray-500 py-12 text-center">Loading…</div>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                {EVENT_TYPES.map((type) => (
                  <Line
                    key={type}
                    type="monotone"
                    dataKey={type}
                    stroke={EVENT_COLORS[type]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className={card}>
        <div className={sectionTitle}>Top routes</div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={th}>Route</th>
                <th className={th}>Page views</th>
                <th className={th}>Filter changes</th>
                <th className={th}>Exports</th>
                <th className={th}>Errors</th>
                <th className={th}>Empty states</th>
                <th className={th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {topRoutes.length === 0 && (
                <tr>
                  <td className={td} colSpan={7}>
                    <span className="text-gray-500">No activity in this range.</span>
                  </td>
                </tr>
              )}
              {topRoutes.map((r) => (
                <tr key={r.route_id}>
                  <td className={td}>
                    <div className="font-medium">{r.route_label ?? r.route_id}</div>
                    <div className="text-xs text-gray-500">{r.path}</div>
                  </td>
                  <td className={td}>{formatNumber(r.counts.page_view ?? 0)}</td>
                  <td className={td}>{formatNumber(r.counts.filter_change ?? 0)}</td>
                  <td className={td}>{formatNumber(r.counts.export ?? 0)}</td>
                  <td className={td}>{formatNumber(r.counts.error_shown ?? 0)}</td>
                  <td className={td}>{formatNumber(r.counts.empty_state ?? 0)}</td>
                  <td className={td + " font-semibold"}>{formatNumber(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={card}>
          <div className={sectionTitle}>Top users</div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={th}>User</th>
                  <th className={th}>Events</th>
                  <th className={th}>Routes</th>
                  <th className={th}>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.length === 0 && (
                  <tr>
                    <td className={td} colSpan={4}>
                      <span className="text-gray-500">No activity in this range.</span>
                    </td>
                  </tr>
                )}
                {topUsers.map((u) => (
                  <tr key={u.user_id}>
                    <td className={td}>
                      <div className="font-medium">{u.username ?? u.user_id.slice(0, 8)}</div>
                    </td>
                    <td className={td}>{formatNumber(u.events)}</td>
                    <td className={td}>{formatNumber(u.routes)}</td>
                    <td className={td}>{moment(u.last_seen).format("MMM D, YYYY")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={card}>
          <div className={sectionTitle}>Top organizations</div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={th}>Organization</th>
                  <th className={th}>Events</th>
                  <th className={th}>Active users</th>
                  <th className={th}>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {topOrgs.length === 0 && (
                  <tr>
                    <td className={td} colSpan={4}>
                      <span className="text-gray-500">No activity in this range.</span>
                    </td>
                  </tr>
                )}
                {topOrgs.map((o) => (
                  <tr key={o.organization_id}>
                    <td className={td}>
                      <div className="font-medium">
                        {o.organization_name ?? `Org ${o.organization_id}`}
                      </div>
                    </td>
                    <td className={td}>{formatNumber(o.events)}</td>
                    <td className={td}>{formatNumber(o.users)}</td>
                    <td className={td}>{moment(o.last_seen).format("MMM D, YYYY")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={card}>
        <div className={sectionTitle}>Recent errors</div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={th}>When</th>
                <th className={th}>Route</th>
                <th className={th}>Code</th>
                <th className={th}>Message</th>
              </tr>
            </thead>
            <tbody>
              {errorRows.length === 0 && (
                <tr>
                  <td className={td} colSpan={4}>
                    <span className="text-gray-500">No errors in this range.</span>
                  </td>
                </tr>
              )}
              {errorRows.map((e) => {
                const payload = (e.payload ?? {}) as Record<string, unknown>;
                const message =
                  typeof payload.message === "string" ? payload.message : "";
                return (
                  <tr key={e.id}>
                    <td className={td}>
                      {moment(e.viewed_at).format("MMM D, h:mm a")}
                    </td>
                    <td className={td}>
                      <div className="font-medium">
                        {e.route_label ?? e.route_id ?? "—"}
                      </div>
                      <div className="text-xs text-gray-500">{e.path}</div>
                    </td>
                    <td className={td}>
                      <code className="text-xs">{e.error_code ?? "—"}</code>
                    </td>
                    <td className={td}>
                      <span className="text-xs">{message}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
